import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { GlobalState } from "../../store";
import { EventType, InventoryType } from "../types";
import { PerkItemType } from "@/shared/modules/PurchaseSeats/SelectPerks/perks.types";

export interface CartEvent {
  eventCode: string;
  products: InventoryType[];
  perks?: PerkItemType[];
  isHeld?: boolean;
  hasNoInventory?: boolean;
  location?: string;
  errorMessage?: string;
  offerId?: number;
  retryCount?: number;
}

export interface CartManagerState {
  cart: CartEvent[];
  cartId?: number;
  totalAmount?: number;
  totalSeats?: number;
  sessionId?: any;
  paymentToken?: string;
  offerId?: number;
  perkTotalAmount?: number;
  orderServiceFee?: number;
  allInPrice?: number;
  basePrice?: number;
  serviceFee?: number;
  cancelCart?: boolean;
}

export const initialState: CartManagerState = {
  cart: [],
};

export const cartManagerState = createSlice({
  name: "cartManager",
  initialState,
  reducers: {
    /*
    +------------------------------------------------------------------------------+
    * [ inventory reducers ]
    +------------------------------------------------------------------------------+ */
    set: (state, action: PayloadAction<CartManagerState>) => {
      return action.payload;
    },

    updateProducts: (
      state,
      action: PayloadAction<{ eventCode: string; product: InventoryType }>
    ) => {
      const { eventCode, product } = action.payload;
      const cartEvent = state.cart.find((cart) => cart.eventCode === eventCode);
      if (!cartEvent) state.cart.push({ eventCode, products: [product] });
      if (cartEvent) {
        let products = cartEvent.products;
        let isExist = products.some((itm) => itm.id === product.id);
        if (isExist) {
          cartEvent.products = products.filter((itm) => itm.id !== product.id);
        } else {
          delete cartEvent?.errorMessage;
          cartEvent.products.push(product);
        }
      }
      if (cartEvent?.products.length === 0) {
        state.cart = state.cart.filter((cart) => cart.eventCode !== eventCode);
      }
    },

    removeProducts: (state, action: PayloadAction<{ eventCode: string }>) => {
      const { eventCode } = action.payload;
      state.cart = state.cart
        .map((event) => {
          if (event.eventCode === eventCode) {
            event.products = [];
            event.isHeld = undefined;
            event.errorMessage = undefined;
            event.location = undefined;
            return event;
          }
          return event;
        })
        .filter((evt) => evt?.products?.length > 0);

      if (state?.cart?.length === 0) {
        state = { cart: [] };
      }
    },

    removeEvent: (state, action: PayloadAction<{ eventCode: string }>) => {
      state.cart = state.cart.filter(
        (itm) => itm.eventCode !== action.payload.eventCode
      );
    },

    removeLocationHeld: (
      state,
      action: PayloadAction<{ locations: string[]; perkLocations: string[] }>
    ) => {
      const seatLocations = action?.payload?.locations;
      const perkLocations = action?.payload?.perkLocations;
      state.cart = state.cart.map((itm) => {
        // removing perks isHeld if location is in perkLocations
        if (itm?.perks?.length) {
          itm.perks = itm.perks.map((perk) => {
            if (perk?.location && perkLocations.includes(perk?.location)) {
              delete perk.isHeld;
              delete perk.location;
              delete perk.errorMessage;
              return perk;
            }
            return perk;
          });
        }

        // removing cart event if location is in seatLocations
        if (itm?.location && seatLocations?.includes(itm?.location)) {
          // reducing the cart total price by the price of the removed location
          const allInPriceToReduce = itm?.products?.reduce((acc, curr) => {
            const itmPrice = curr?.totalAllInPrice
              ? Number(curr?.totalAllInPrice)
              : Number(curr?.allInPrice) * Number(curr?.seatCount || 0);
            return acc + itmPrice;
          }, 0);

          const basePriceToReduce = itm?.products?.reduce((acc, curr) => {
            const itmPrice = curr?.totalBasePrice
              ? Number(curr?.totalBasePrice)
              : Number(curr?.basePrice) * Number(curr?.seatCount || 0);
            return acc + itmPrice;
          }, 0);

          const serviceFeeToReduce = itm?.products?.reduce((acc, curr) => {
            const itmServiceFee = curr?.totalServiceFee
              ? Number(curr?.totalServiceFee)
              : Number(curr?.serviceFee || 0) * Number(curr?.seatCount || 0);
            return acc + itmServiceFee;
          }, 0);

          // reducing the cart service fee by the price of the removed location
          if (state?.serviceFee) {
            state.serviceFee =
              Number(state?.serviceFee || 0) - serviceFeeToReduce;
          }

          if (state?.allInPrice) {
            state.allInPrice = state.allInPrice - allInPriceToReduce;
          }
          if (state?.basePrice) {
            state.basePrice = state.basePrice - basePriceToReduce;
          }

          const seatCountToReduce = itm?.products?.reduce((acc, curr) => {
            return acc + Number(curr?.seatCount || 0);
          }, 0);
          if (state?.totalSeats) {
            state.totalSeats = Number(state?.totalSeats) - seatCountToReduce;
          }

          if (itm?.perks?.length) {
            return { eventCode: itm.eventCode, products: [], perks: itm.perks };
          }
          return { eventCode: itm.eventCode, products: [] };
        }

        return itm;
      });
    },
    setCart: (state, action: PayloadAction<CartManagerState>) => {
      return action.payload;
    },

    setSessionId: (state, action: PayloadAction<{ sessionId: string }>) => {
      state.sessionId = action.payload.sessionId;
    },

    /*
    +------------------------------------------------------------------------------+
    * [ perks reducers ]
    +------------------------------------------------------------------------------+ */
    // add or removes perkItem in the perks array to the relevant cartEvent
    togglePerkItemSelection: (
      state,
      action: PayloadAction<{ perkItem: PerkItemType }>
    ) => {
      const perkItemToToggle = action.payload.perkItem;
      const perkGameEventCode = perkItemToToggle.gameEventCode;

      state.cart.map((cartEvent) => {
        if (cartEvent.eventCode == perkGameEventCode) {
          if (!cartEvent.perks) cartEvent.perks = [];
          const isExist = cartEvent.perks.some(itm => itm.id == perkItemToToggle.id); // prettier-ignore
          if (isExist) {
            cartEvent.perks = cartEvent.perks.filter(itm => itm.id !== perkItemToToggle.id); // prettier-ignore
          } else {
            cartEvent.perks.push(perkItemToToggle);
          }
        }
      });
    },

    addPerkItemSelection: (
      state,
      action: PayloadAction<{ perkItem: PerkItemType }>
    ) => {
      const perkItemToAdd = action.payload.perkItem;
      const perkGameEventCode = perkItemToAdd.gameEventCode;

      state.cart.map((cartEvent) => {
        if (cartEvent.eventCode == perkGameEventCode) {
          if (!cartEvent.perks) cartEvent.perks = [];
          const isExist = cartEvent.perks.some(itm => itm.id == perkItemToAdd.id); // prettier-ignore
          if (isExist) return;
          cartEvent.perks.push(perkItemToAdd);
        }
      });
    },

    // prettier-ignore
    updatePerkItem: (state, action: PayloadAction<Partial<PerkItemType>>) => {
      let { gameEventCode, id, ...perkItem } = action.payload;
      state.cart.map((cartEvent) => {
        if (cartEvent.eventCode == gameEventCode) {
          if (!cartEvent.perks) cartEvent.perks = [];
          cartEvent.perks = cartEvent.perks.map((itm) => {
            if (itm.id == id) return Object.assign(itm, perkItem);
            return itm;
          })
        }
      });
    },

    // add multiple perkItems in the perks array to the relevant cartEvent
    addPerkItemMulti: (
      state,
      action: PayloadAction<{ perkItems: PerkItemType[] }>
    ) => {
      const perkItems = action.payload.perkItems;
      perkItems.forEach((perkItem) => {
        state.cart.forEach((cartEvent) => {
          if (cartEvent.eventCode == perkItem.gameEventCode) {
            if (!cartEvent.perks) cartEvent.perks = [];
            const isExist = cartEvent.perks.some(itm => itm.id == perkItem.id); // prettier-ignore
            if (!isExist) {
              cartEvent.perks.push(perkItem);
            }
          }
        });
      });
    },

    // remove multiple perkItems in the perks array to the relevant cartEvent
    removePerkItemMulti: (
      state,
      action: PayloadAction<{ perkItems: PerkItemType[] }>
    ) => {
      const perkItems = action.payload.perkItems;
      perkItems.forEach((perkItem) => {
        state.cart.forEach((cartEvent) => {
          if (cartEvent.eventCode == perkItem.gameEventCode) {
            cartEvent.perks = cartEvent?.perks?.filter(itm => itm.id !== perkItem.id) || []; // prettier-ignore
          }
          if (cartEvent.perks?.length === 0) delete cartEvent.perks;
        });
      });
    },

    // toggles order level perks items
    // prettier-ignore
    toggleOLPerkItemSelection: (
      state,
      action: PayloadAction<{ perkItem: PerkItemType }>
    ) => {
      // toggling add/remove perkItem in the perks array to the hard coded order-level event
      const perkItemToToggle = action.payload.perkItem;
      const isOLEventInCart = state.cart.some((itm) => itm?.eventCode == "order-level");
      if(!isOLEventInCart) state.cart.push({ eventCode: "order-level", perks: [], products : [], isHeld : true }); 
      state.cart.forEach((cartEvent) => {
        if(cartEvent.eventCode == "order-level"){
          if (!cartEvent.perks) cartEvent.perks = [];
          const isExist = cartEvent.perks.some(itm => itm.id == perkItemToToggle.id);
          if (isExist) {
            cartEvent.perks = cartEvent.perks.filter(itm => itm.id !== perkItemToToggle.id);
          } else {
            cartEvent.perks = [...cartEvent.perks, perkItemToToggle];
          }
        }
      });

      // removing ol event from cart if no perks
      const olEvent = state.cart.find((itm) => itm?.eventCode == "order-level");
      if(olEvent?.perks?.length === 0) state.cart = state.cart.filter((itm) => itm?.eventCode !== "order-level");
    },

    // removes failed perks from the cart
    deleteAllFailedPerks: (state, action: PayloadAction<undefined>) => {
      state.cart = state.cart.map((cartEvent) => {
        cartEvent.perks = cartEvent.perks?.filter((perk) => !perk.errorMessage);
        return cartEvent;
      });
    },
  },
});

const selector = (s: GlobalState) => s.data.purchaseSeats.cartManager;

export const selectCartManager = (s: GlobalState) => {
  return s.data.purchaseSeats.cartManager;
};

export const selectCartEvents = createSelector(selector, (state) => {
  return state.cart;
});

/*
+------------------------------------------------------------------------------+
* [ inventory selectors]
+------------------------------------------------------------------------------+ */
export const selectProducts = (eventCode: string | undefined) => {
  return createSelector(selector, (state) => {
    const cartEvent = state.cart.find((cart) => cart.eventCode === eventCode);
    if (eventCode && cartEvent) return cartEvent.products;
    else return [];
  });
};

export const selectCartEventActive = (event: EventType | null) => {
  return createSelector(selector, (state) => {
    const eventCode = event?.eventCode;
    const cartEvent = state.cart.find((itm) => itm.eventCode === eventCode);
    if (eventCode && cartEvent) return cartEvent;
    return null;
  });
};

/*
+------------------------------------------------------------------------------+
* [ perks selectors] ]
+------------------------------------------------------------------------------+ */

export const selectPerkItemSelected = (perkItem: PerkItemType) => {
  return createSelector(selector, (state) => {
    let cartEventCode = perkItem.gameEventCode;
    const perkId = perkItem.id;
    const cartEvent = state.cart.find((cart) => cart?.eventCode == cartEventCode); // prettier-ignore
    return cartEvent?.perks?.find((itm) => itm?.id == perkId) || null;
  });
};

export const selectIsAnyPerkItemSelected = (perkItems: PerkItemType[]) => {
  return createSelector(selector, (state) => {
    return perkItems.some((perkItem) => {
      const cartEvent = state.cart.find((cartEvent) => cartEvent?.eventCode == perkItem.gameEventCode); // prettier-ignore
      return cartEvent?.perks?.some((itm) => itm.id == perkItem.id);
    });
  });
};

export const selectPerkItemSelectedMulti = (perkItems: PerkItemType[]) => {
  return createSelector(selector, (state) => {
    return perkItems.every((perkItem) => {
      const cartEvent = state.cart.find((cartEvent) => cartEvent?.eventCode == perkItem.gameEventCode); // prettier-ignore
      return cartEvent?.perks?.some((itm) => itm.id == perkItem.id);
    });
  });
};

export const selectPerkItemSelectedByPerkCode = (perkCode: string) => {
  return createSelector(selector, (state) => {
    const perksItemsSelected = [] as PerkItemType[];
    state.cart.forEach((cartEvent) => {
      if (cartEvent?.perks?.length) {
        perksItemsSelected.push(...cartEvent.perks);
      }
    });
    return perksItemsSelected.filter(
      (itm: PerkItemType) => itm.perkCode == perkCode
    );
  });
};

export const selectPerkFailedCartEvents = createSelector(selector, (state) => {
  return state.cart
    .filter(({ perks }) => {
      return perks?.some((perk) => !!perk.errorMessage);
    })
    .map((cartEvent) => {
      return {
        ...cartEvent,
        perks: cartEvent.perks?.filter((perk) => !!perk.errorMessage),
      };
    });
});
